import * as React from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'

import { cn } from '../../lib/utils'

function Popover({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />
}

function PopoverTrigger({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
}

function PopoverContent({
  className,
  align = 'center',
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[10005] w-72 origin-(--radix-popover-content-transform-origin) rounded-md border p-4 shadow-md outline-hidden',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  )
}

function PopoverContentWrapper({
  className,
  onFocusOutside,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverContent
      {...props}
      onFocusOutside={(event) => {
        // Prevent this large panel from closing when focus moves to a portal (e.g. a Dialog).
        // The panel should only close via an explicit outside pointer-down.
        event.preventDefault()
        onFocusOutside?.(event)
      }}
      className={cn(
        'w-[calc(100vw)] md:w-100 h-[calc(100dvh-64px)] md:h-[calc(100dvh-100px)] mt-[50px] overflow-y-auto rounded-none md:rounded-md z-[10000]',
        className,
      )}
    />
  )
}

function PopoverTriggerWrapper({
  children,
  open,
  active,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger> & {
  children: React.ReactNode
  open: boolean
  active?: boolean
}) {
  return (
    <PopoverTrigger asChild {...props}>
      <button
        className={`cursor-pointer w-full md:w-16 md:h-16 md:rounded-full md:shadow-sm md:border md:border-gray-200 flex items-center justify-center transition-colors relative ${
          open || active
            ? 'bg-main text-white hover:bg-main'
            : 'bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        {children}
      </button>
    </PopoverTrigger>
  )
}

function PopoverAnchor({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />
}

export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverAnchor,
  PopoverContentWrapper,
  PopoverTriggerWrapper,
}
